/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { loadStatus, cacheBurstLogKeys, ScrollDirection } from "constants/enums";
import { getPagesCacheWindow, getScrollDirection } from "selectors/documentSelectors";
import { isIOS } from "dc-preview-utils/src/Utils/DeviceUtils";
import { PAGES_TO_LOAD_IN_SCROLL_DIRECTION } from "constants/constants";

class CacheManager {
  constructor({
    itemType,
    docAPI,
    stores,
    itemsInView,
    sizeOfItemsLoaded,
    totalItems,
    getItemStatus,
    getItemBitmapSize,
    getItemZoomLevel,
    loadItem,
    unloadItem,
    revokeItem,
    maxBitmapTotalSize,
    cacheWindow,
    cachePriority
  }) {
    this.itemType = itemType;
    this.docAPI = docAPI;
    this.stores = stores;
    this.itemsInView = itemsInView;
    this.sizeOfItemsLoaded = sizeOfItemsLoaded;
    this.getItemZoomLevel = getItemZoomLevel;
    this.totalItems = totalItems;
    this.maxBitmapTotalSize = maxBitmapTotalSize;
    this.getItemStatus = getItemStatus;
    this.getItemBitmapSize = getItemBitmapSize;
    this.numberOfPagesCached = 0;
    this.loadItem = loadItem;
    this.unloadItem = unloadItem;
    this.revokeItem = revokeItem;
    this.cacheWindow = cacheWindow;
    this.cachePriority = cachePriority;
    this.lastCacheBurstTime = -1;
  }

  _unloadItem(index) {
    this.unloadItem(index);
    this.revokeItem && this.revokeItem(index);
  }

  _getItemsInView() {
    return this.itemsInView(this.stores);
  }

  _moveItems(source, target, count) {
    const items = source.splice(0, count);
    items.forEach(item => {
      if (this.getItemStatus(this.stores, item) !== loadStatus.LOADED) {
        target.push(item);
      }
    });
  }

  _getItemsToCacheHelper(
    itemsInForwardDirection,
    itemsInBackwardDirection,
    direction,
    itemsCountToLoadInScrollDirectionContinuously
  ) {
    let itemsToCache = [];
    const itemsInScrollDirection =
      direction === ScrollDirection.FORWARD ? itemsInForwardDirection : itemsInBackwardDirection;
    const itemsInOppositeDirection =
      direction === ScrollDirection.BACKWARD ? itemsInForwardDirection : itemsInBackwardDirection;

    // move items from both arrays to the final array (itemsToCache) in an alternate fashion. Just take more items from the array in the scrolling direction
    while (itemsInScrollDirection.length || itemsInOppositeDirection.length) {
      if (itemsInScrollDirection.length) {
        this._moveItems(itemsInScrollDirection, itemsToCache, itemsCountToLoadInScrollDirectionContinuously);
      }

      if (itemsInOppositeDirection.length) {
        this._moveItems(itemsInOppositeDirection, itemsToCache, 1);
      }
    }

    return itemsToCache;
  }

  _getItemsToCache(itemsInView) {
    const itemsCountToLoadInScrollDirectionContinuously =
      this.itemType === "page" ? PAGES_TO_LOAD_IN_SCROLL_DIRECTION : 1;
    const direction = getScrollDirection(this.stores);
    // In the context of iOS, we are maintaining a cache window size of 2 due to memory issues.
    const cacheWindow = this.itemType === "page" && !isIOS() ? getPagesCacheWindow(this.stores) : this.cacheWindow;
    if (!itemsInView) {
      itemsInView = this._getItemsInView();
    }

    const { start: viewStart, end: viewEnd } = itemsInView;
    const totItemToCacheInScrollDirection = cacheWindow;
    const totItemToCacheInOppositeDirection = Math.ceil(cacheWindow / itemsCountToLoadInScrollDirectionContinuously);
    const cacheWindowInForwardDirection =
      direction === ScrollDirection.FORWARD ? totItemToCacheInScrollDirection : totItemToCacheInOppositeDirection;
    const cacheWindowInBackwardDirection =
      direction === ScrollDirection.BACKWARD ? totItemToCacheInScrollDirection : totItemToCacheInOppositeDirection;

    let itemsInForwardDirection = [],
      itemsInBackwardDirection = [];

    for (let i = 1; i <= cacheWindowInForwardDirection; ++i) {
      const itemIndex = viewEnd + i;
      if (itemIndex < this.totalItems) {
        itemsInForwardDirection.push(itemIndex);
      }
    }

    for (let i = 1; i <= cacheWindowInBackwardDirection; ++i) {
      const itemIndex = viewStart - i;
      if (itemIndex >= 0) {
        itemsInBackwardDirection.push(itemIndex);
      }
    }

    return this._getItemsToCacheHelper(
      itemsInForwardDirection,
      itemsInBackwardDirection,
      direction,
      itemsCountToLoadInScrollDirectionContinuously
    );
  }

  clearCache() {
    const itemsLoadedSize = this.sizeOfItemsLoaded(this.stores);
    let itemsInView = this._getItemsInView();
    let totalItemsLoadedSize = itemsLoadedSize;
    for (let i = itemsInView.start; i <= itemsInView.end; ++i) {
      totalItemsLoadedSize -= this.getItemBitmapSize(this.stores, i);
    }
    if (totalItemsLoadedSize > this.maxBitmapTotalSize) {
      const startTime = Date.now();
      if (this.lastCacheBurstTime !== -1) {
        const diffBwConsecutiveCacheBurst = Date.now() - this.lastCacheBurstTime;
        this.stores.documentStore.updateCacheBurstInfo(cacheBurstLogKeys.DIFF_CONS_BURST, diffBwConsecutiveCacheBurst);
      }
      this.lastCacheBurstTime = Date.now();
      let itemsSizeToUnload = totalItemsLoadedSize - this.maxBitmapTotalSize;
      let start = 0,
        end = this.totalItems - 1;

      while (end >= start && itemsSizeToUnload > 0) {
        while (start < itemsInView.start && this.getItemStatus(this.stores, start) !== loadStatus.LOADED) {
          start++;
        }

        while (end > itemsInView.end && this.getItemStatus(this.stores, end) !== loadStatus.LOADED) {
          end--;
        }

        if (start === itemsInView.start) {
          if (end === itemsInView.end) {
            break;
          }
          if (this.getItemStatus(this.stores, end) === loadStatus.LOADED) {
            itemsSizeToUnload -= this.getItemBitmapSize(this.stores, end);
            this._unloadItem(end);
          }
        } else if (end === itemsInView.end) {
          if (this.getItemStatus(this.stores, start) === loadStatus.LOADED) {
            itemsSizeToUnload -= this.getItemBitmapSize(this.stores, start);
            this._unloadItem(start);
          }
        } else if (
          this.getItemStatus(this.stores, start) === loadStatus.LOADED &&
          this.getItemStatus(this.stores, end) === loadStatus.LOADED
        ) {
          if (itemsInView.start - start < end - itemsInView.end) {
            itemsSizeToUnload -= this.getItemBitmapSize(this.stores, end);
            this._unloadItem(end);
          } else {
            itemsSizeToUnload -= this.getItemBitmapSize(this.stores, start);
            this._unloadItem(start);
          }
        }
      }
      const timeTakenInCacheFix = Date.now() - startTime;
      this.stores.documentStore.updateCacheBurstInfo(cacheBurstLogKeys.TIME_CACHE_FIX, timeTakenInCacheFix);
    }
  }

  cacheItems(itemsInView) {
    if (!itemsInView) {
      itemsInView = this._getItemsInView();
    }
    const itemsToCache = this._getItemsToCache(itemsInView);
    const stores = this.stores;
    const itemToCache = itemsToCache[0];
    if (itemToCache !== undefined) {
      this.loadItem(itemToCache, this.getItemZoomLevel(stores, itemToCache), itemsInView, this.cachePriority, {
        type: "cache",
        itemsToCache: itemsToCache.slice(0)
      });
    }
  }
}

export { CacheManager };
